import React from "react";
import { Box, Typography, Chip, Stack, alpha } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  PhotoCamera as CameraIcon,
  Group as BuddyIcon,
  Visibility as EyeIcon,
} from "@mui/icons-material";
import ScubaDivingIcon from "@mui/icons-material/ScubaDiving";

import { formatDistanceToNow } from "date-fns";

// Glassmorphic styled components
const GlassActivityContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.06
  )} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  borderRadius: 12,
  margin: theme.spacing(1, 0),
  position: "relative",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.2
    )}, transparent)`,
  },
  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.1
    )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    transform: "translateY(-2px)",
    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
}));

const IconWrapper = styled(Box)(({ theme, activityType }) => {
  const getIconColor = (type) => {
    switch (type) {
      case "dive_logged":
        return theme.palette.primary.main;
      case "creature_spotted":
        return theme.palette.success.main;
      case "buddy_dive":
        return theme.palette.secondary.main;
      case "media_uploaded":
        return theme.palette.warning.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const iconColor = getIconColor(activityType);

  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${alpha(iconColor, 0.15)} 0%, ${alpha(
      iconColor,
      0.08
    )} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(iconColor, 0.3)}`,
    flexShrink: 0,
    position: "relative",
    transition: "all 0.3s ease",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "2px",
      left: "2px",
      right: "2px",
      bottom: "2px",
      borderRadius: "50%",
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.common.white,
        0.1
      )}, transparent)`,
    },
    "& .MuiSvgIcon-root": {
      color: iconColor,
      fontSize: 20,
      zIndex: 1,
      position: "relative",
    },
  };
});

const GlassChip = styled(Chip)(({ theme, chipType }) => {
  const getChipColors = (type) => {
    switch (type) {
      case "depth":
        return {
          bg: alpha(theme.palette.info.main, 0.1),
          border: alpha(theme.palette.info.main, 0.2),
          color: theme.palette.info.main,
        };
      case "rating":
        return {
          bg: alpha(theme.palette.warning.main, 0.1),
          border: alpha(theme.palette.warning.main, 0.2),
          color: theme.palette.warning.main,
        };
      case "visibility":
        return {
          bg: alpha(theme.palette.success.main, 0.1),
          border: alpha(theme.palette.success.main, 0.2),
          color: theme.palette.success.main,
        };
      case "creature":
        return {
          bg: alpha(theme.palette.success.main, 0.12),
          border: alpha(theme.palette.success.main, 0.25),
          color: theme.palette.success.dark,
        };
      default:
        return {
          bg: alpha(theme.palette.primary.main, 0.1),
          border: alpha(theme.palette.primary.main, 0.2),
          color: theme.palette.primary.main,
        };
    }
  };

  const colors = getChipColors(chipType);

  return {
    background: colors.bg,
    backdropFilter: "blur(8px)",
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    color: colors.color,
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 28,
    transition: "all 0.2s ease",
    "& .MuiChip-label": {
      padding: "0 8px",
    },
    "&:hover": {
      background: alpha(colors.color, 0.15),
      border: `1px solid ${alpha(colors.color, 0.3)}`,
      transform: "scale(1.05)",
    },
  };
});

const DiverName = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.95rem",
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  display: "inline",
}));

const LocationText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: alpha(theme.palette.text.primary, 0.9),
  display: "inline",
}));

const MetaText = styled(Typography)(({ theme }) => ({
  color: alpha(theme.palette.text.secondary, 0.8),
  fontSize: "0.75rem",
  fontWeight: 500,
  letterSpacing: "0.25px",
}));

const ActivityItem = ({ activity }) => {
  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case "dive_logged":
        return <ScubaDivingIcon />;
      case "creature_spotted":
        return <EyeIcon />;
      case "buddy_dive":
        return <BuddyIcon />;
      case "media_uploaded":
        return <CameraIcon />;
      default:
        return <ScubaDivingIcon />;
    }
  };

  const formatActivityContent = () => {
    const { activityType, metadata, diverInfo } = activity;
    const diverName = diverInfo?.name || "Unknown Diver";

    switch (activityType) {
      case "dive_logged":
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.5 }}>
              <DiverName component="span">{diverName}</DiverName> logged a dive at{" "}
              <LocationText component="span">{metadata.divesiteName}</LocationText>
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", gap: 1 }}
            >
              {metadata.maxDepth && (
                <GlassChip
                  chipType="depth"
                  label={`${metadata.maxDepth}m depth`}
                  size="small"
                  clickable={false}
                  onClick={() => {}}
                />
              )}
              {metadata.diveRating && (
                <GlassChip
                  chipType="rating"
                  label={`${"⭐".repeat(metadata.diveRating)} rating`}
                  size="small"
                  clickable={false}
                  onClick={() => {}}
                />
              )}
              {metadata.visibilityRating && (
                <GlassChip
                  chipType="visibility"
                  label={`${metadata.visibilityRating}/5 visibility`}
                  size="small"
                  clickable={false}
                  onClick={() => {}}
                />
              )}
            </Stack>
          </Box>
        );

      case "creature_spotted":
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.5 }}>
              <DiverName component="span">{diverName}</DiverName> spotted marine life at{" "}
              <LocationText component="span">{metadata.divesiteName}</LocationText>
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", gap: 1 }}
            >
              {metadata.creatureNames?.map((creature, index) => (
                <GlassChip
                  key={index}
                  chipType="creature"
                  label={creature}
                  size="small"
                  clickable={false}
                  onClick={() => {}}
                />
              ))}
            </Stack>
          </Box>
        );

      case "buddy_dive":
        return (
          <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
            <DiverName component="span">{diverName}</DiverName> dove with{" "}
            <LocationText component="span">
              {metadata.buddyName || "an unknown buddy"}
            </LocationText>{" "}
            at <LocationText component="span">{metadata.divesiteName}</LocationText>
          </Typography>
        );

      case "media_uploaded":
        return (
          <Box>
            <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
              <DiverName component="span">{diverName}</DiverName> uploaded {metadata.mediaCount}{" "}
              photo
              {metadata.mediaCount > 1 ? "s" : ""} from{" "}
              <LocationText component="span">{metadata.divesiteName}</LocationText>
            </Typography>
          </Box>
        );

      default:
        return (
          <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
            <DiverName component="span">{diverName}</DiverName> had some diving activity
          </Typography>
        );
    }
  };

  return (
    <GlassActivityContainer>
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        {/* Activity Icon */}
        <IconWrapper activityType={activity.activityType}>
          {getActivityIcon(activity.activityType)}
        </IconWrapper>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Main activity description */}
          <Box sx={{ mb: 1.5 }}>{formatActivityContent()}</Box>

          {/* Meta information */}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              flexWrap: "wrap",
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <MetaText>
              {formatDistanceToNow(new Date(activity.createdAt), {
                addSuffix: true,
              })}
            </MetaText>
            {activity.metadata.diveDate && (
              <MetaText>
                Dive:{" "}
                {new Date(activity.metadata.diveDate).toLocaleDateString()}
              </MetaText>
            )}
            {activity.metadata.divesiteLocation && (
              <MetaText
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                📍 {activity.metadata.divesiteLocation}
              </MetaText>
            )}
          </Stack>
        </Box>
      </Box>
    </GlassActivityContainer>
  );
};

export default ActivityItem;
